import type { AccountTemplates } from '@repo/api-types/docusign';
import { AccountsApi, Configuration, type SignRequest, SignRequestsApi } from '@repo/api-types/docusign';
import { getHeaders } from '@repo/utils';
import type { CreateSignParams, DocumentsParams, SearchSignsParams } from './docusign.types.ts';

type DocusignHeaderParams = {
    'x-bucode': string;
    'x-locale': string;
    'x-request-id': string;
    'x-countrycode': string;
};
const getReqHeaders = async () => {
    const headers = await getHeaders();
    return Object.fromEntries(headers.entries());
};

function validateHeaders(
    headers: Record<string, string | undefined>
): asserts headers is Record<string, string> & DocusignHeaderParams {
    const required = ['x-bucode', 'x-locale', 'x-request-id', 'x-countrycode'] as const;
    const missing = required.filter((k) => !headers[k]?.trim());
    if (missing.length > 0) {
        throw new Error(`Missing or empty required headers: ${missing.join(', ')}`);
    }
}

export const getSignRequestWithFilters = async (
    params: SearchSignsParams,
    basePath: string
): Promise<SignRequest[]> => {
    const headers = await getReqHeaders();
    validateHeaders(headers);
    const api = new SignRequestsApi(
        new Configuration({
            basePath,
            headers,
        })
    );

    return api.signRequestControllerGetSignRequestWithFilters({
        ...params,
        x_countrycode: headers['x-countrycode'],
        x_bucode: headers['x-bucode'],
        x_locale: headers['x-locale'],
        x_request_id: headers['x-request-id'],
    });
};

export const createSignRequestWithTemplate = async (
    params: CreateSignParams,
    basePath: string
): Promise<SignRequest> => {
    const headers = await getReqHeaders();
    validateHeaders(headers);
    const api = new SignRequestsApi(new Configuration({ basePath, headers }));
    return api.signRequestControllerCreateSignRequestWithTemplate({
        ...params,
        x_countrycode: headers['x-countrycode'],
        x_bucode: headers['x-bucode'],
        x_locale: headers['x-locale'],
        x_request_id: headers['x-request-id'],
    });
};

export const getSignRequestDocuments = async (params: DocumentsParams, basePath: string): Promise<Blob> => {
    const headers = await getReqHeaders();
    validateHeaders(headers);
    const api = new SignRequestsApi(
        new Configuration({
            basePath,
            headers,
        })
    );

    return api.signRequestControllerGetDocuments({
        ...params,
        x_countrycode: headers['x-countrycode'],
        x_bucode: headers['x-bucode'],
        x_locale: headers['x-locale'],
        x_request_id: headers['x-request-id'],
    });
};

export const getAccountTemplates = async (basePath: string): Promise<AccountTemplates> => {
    const headers = await getReqHeaders();
    validateHeaders(headers);
    const api = new AccountsApi(new Configuration({ basePath, headers }));
    return api.accountsControllerGetMarketTemplates({
        x_countrycode: headers['x-countrycode'],
        x_bucode: headers['x-bucode'],
        x_locale: headers['x-locale'],
        x_request_id: headers['x-request-id'],
    });
};
